import {
  useLocalSessionId,
  useMediaTrack,
  useParticipantProperty,
} from '@daily-co/daily-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isTrackOff } from '../lib/participants';

const minVoiceFrequency = 85; // Hz - lowest base frequency for deep male voices
const maxVoiceFrequency = 2100; // Hz – only very very high pitched kid voices

const threshold = 255 * (2 / 3); // 255 represents maximum intensity

export const useLocalAudioStatus = () => {
  const localSessionId = useLocalSessionId();
  const clonedTrackRef = useRef<MediaStreamTrack>(null);
  const voiceIntensityRef = useRef<number>(0);
  const micTrack = useMediaTrack(localSessionId, 'audio');

  useEffect(() => {
    if (!micTrack.persistentTrack) return;
    clonedTrackRef.current = micTrack.persistentTrack.clone();
    clonedTrackRef.current.enabled = true;

    const audioCtx = new AudioContext();
    const analyserNode = audioCtx.createAnalyser();
    analyserNode.fftSize = 256;
    const source = audioCtx.createMediaStreamSource(
      new MediaStream([clonedTrackRef.current])
    );
    source.connect(analyserNode);
    const buffer = new Uint8Array(analyserNode.frequencyBinCount);

    let frame: ReturnType<typeof requestAnimationFrame>;
    function checkVolume() {
      analyserNode.getByteFrequencyData(buffer);
      const humanVoiceRange = buffer
        .slice(
          Math.floor(
            minVoiceFrequency /
              (audioCtx.sampleRate / analyserNode.frequencyBinCount)
          ),
          Math.ceil(
            maxVoiceFrequency /
              (audioCtx.sampleRate / analyserNode.frequencyBinCount)
          )
        )
        .filter((f) => f > 0);
      // get average intensity for human voice frequency buckets
      voiceIntensityRef.current =
        humanVoiceRange.reduce((sum, intensity) => sum + intensity, 0) /
        humanVoiceRange.length;
      frame = requestAnimationFrame(checkVolume);
    }
    checkVolume();

    return () => {
      cancelAnimationFrame(frame);
      clonedTrackRef.current.stop();
      clonedTrackRef.current = null;
      audioCtx.close();
    };
  }, [micTrack.persistentTrack]);

  const audioTrackState = useParticipantProperty(
    localSessionId,
    'tracks.audio.state'
  );
  const isMicMuted = isTrackOff(audioTrackState);

  const [isSpeakingButMuted, setIsSpeakingButMuted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  /**
   * Resets dismissed state when mic is unmuted.
   */
  useEffect(() => {
    if (isMicMuted) return;
    setDismissed(false);
  }, [isMicMuted]);

  /**
   * Sets up interval when mic is muted and status is not dismissed.
   * When voice intensity crosses threshold, speaking but muted state is enabled.
   */
  useEffect(() => {
    if (!isMicMuted) {
      setIsSpeakingButMuted(false);
      return;
    }
    if (isSpeakingButMuted || dismissed) return;
    const interval = setInterval(() => {
      if (voiceIntensityRef.current > threshold) {
        setIsSpeakingButMuted(true);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [dismissed, isMicMuted, isSpeakingButMuted]);

  /**
   * Resets the states.
   */
  const clear = useCallback(() => {
    setIsSpeakingButMuted(false);
    setDismissed(false);
  }, []);

  /**
   * Dismisses the currently detected voice while muted state.
   * Can be reset with the `clear` method.
   */
  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  return {
    clear,
    dismiss,
    isMicMuted,
    isSpeakingButMuted: isSpeakingButMuted && !dismissed,
  };
};
